import React, { useRef, useState } from 'react';
import SEOHelmet from '@/components/SEOHelmet';
import { NavBar } from '@/components/navbar';
import { Footer } from '@/components/SiteFooter';
import { motion, useScroll, useTransform } from 'framer-motion';
import { Button } from '@/components/ui/button';
import {
  ArrowRight,
  AtSign,
  ChevronDown,
  ChevronUp,
  Lightbulb,
  MapPin,
  Sparkles,
  Target,
} from 'lucide-react';
import { Link } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import { Card } from '@/components/ui/card';
import { Badge } from '@/components/ui/badge';
import PageViewTracker from '@/components/analytics/PageViewTracker';
import { trackEvent } from '@/utils/analytics';
import { GlowBackground } from '@/components/GlowBackground.tsx';

const About = () => {
  const { t } = useTranslation('common');
  const pageRef = useRef<HTMLDivElement>(null);
  const [activeAccordion, setActiveAccordion] = useState<number | null>(null);
  const { scrollYProgress } = useScroll({
    target: pageRef,
    offset: ['start start', 'end end'],
  });

  const headerOpacity = useTransform(scrollYProgress, [0, 0.1], [1, 0.9]);

  const toggleAccordion = (index: number) => {
    const newValue = activeAccordion === index ? null : index;
    setActiveAccordion(newValue);
    if (newValue !== null && faqItems[index]) {
      trackEvent('FAQ', 'Open', faqItems[index].question);
    }
  };

  const coreValueIcons = [
    <Target className="h-6 w-6 text-[#229302]" />,
    <Lightbulb className="h-6 w-6 text-[#229302]" />,
    <Sparkles className="h-6 w-6 text-[#229302]" />,
  ];

  interface CoreValue {
    title: string;
    description: string;
  }

  const coreValues =
    (t('about.aboutPage.coreValues', { returnObjects: true }) as CoreValue[]) || [];

  interface TeamMember {
    name: string;
    title: string;
    image: string;
    bio: string;
    linkedin: string;
  }

  const teamMembers =
    (t('about.aboutPage.teamSection', { returnObjects: true }) as TeamMember[]) || [];

  interface FAQ {
    question: string;
    answer: string;
  }

  const faqItems = (t('about.aboutPage.faqs', { returnObjects: true }) as FAQ[]) || [];

  return (
    <div ref={pageRef}>
      <SEOHelmet path="/about" />
      <PageViewTracker path="/about" title="About Verdea - Our Mission & Values" />

      <NavBar />
      <GlowBackground colorScheme={'green'} />

      <main className="relative pt-24 pb-16 min-h-screen overflow-hidden">
        {/* Background Elements - Green theme */}
        <div className="fixed inset-0 z-0 overflow-hidden pointer-events-none">
          {/* Primary gradient blur - Dark green theme */}
          <div
            className="absolute top-0 right-0 w-[50vw] h-[50vh] blur-3xl opacity-30"
            style={{
              background:
                'radial-gradient(circle at center, rgba(24, 76, 6, 0.3) 0%, rgba(24, 76, 6, 0) 70%)', // Dark green #184c06
              transform: 'translate(20%, -20%)',
            }}
          />

          {/* Secondary gradient blur */}
          <div
            className="absolute bottom-0 left-0 w-[40vw] h-[40vh] blur-3xl opacity-20"
            style={{
              background:
                'radial-gradient(circle at center, rgba(34, 147, 2, 0.3) 0%, rgba(34, 147, 2, 0) 70%)', // Light green #229302
              transform: 'translate(-20%, 20%)',
            }}
          />

          {/* Subtle dot grid pattern */}
          <div
            className="absolute inset-0 opacity-[0.015]"
            style={{
              backgroundImage: 'radial-gradient(rgba(34, 147, 2, 0.2) 1px, transparent 1px)', // Light green #229302
              backgroundSize: '40px 40px',
            }}
          />
        </div>

        <div className="container mx-auto px-4 md:px-6 relative z-10">
          <motion.div
            style={{ opacity: headerOpacity }}
            initial={{ opacity: 0, y: 10 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.6 }}
            className="max-w-4xl mx-auto text-center mb-20"
          >
            <Badge
              variant="outline"
              className="px-4 py-1.5 mb-6 bg-green-50 border-green-100 text-green-800 text-sm font-medium dark:bg-green-900/20 dark:border-green-800/30 dark:text-green-400"
            >
              {t('about.aboutPage.heroSection.badge')}
            </Badge>
            <h1 className="text-4xl md:text-6xl font-medium tracking-tight text-gray-900 dark:text-gray-100 mb-6 leading-tight">
              {t('about.aboutPage.heroSection.heading')}
              <br />
              <span className="bg-gradient-to-r from-[#184c06] via-[#1b6504] to-[#229302] bg-clip-text text-transparent font-semibold">
                {t('about.aboutPage.heroSection.headingHighlight')}
              </span>
            </h1>
            <p className="text-lg md:text-xl text-gray-500 dark:text-gray-400 mb-8 max-w-3xl mx-auto leading-relaxed">
              {t('about.aboutPage.heroSection.subheading')}
            </p>
          </motion.div>

          {/* Our Mission Section */}
          <div className="relative mb-16 z-10">
            <motion.div
              initial={{ opacity: 0, y: 20 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ duration: 0.6, delay: 0.1 }}
              className="max-w-4xl mx-auto"
            >
              <Card className="p-8 mb-8 bg-white/80 backdrop-blur-sm dark:bg-gray-800/80">
                <p className="text-xl md:text-2xl font-medium text-[#184c06] dark:text-green-400 mb-6">
                  "{t('about.aboutPage.missionSection.quote')}"
                </p>
                <div className="prose prose-lg max-w-none text-gray-600 dark:text-gray-400">
                  <p className="mb-6 text-lg leading-relaxed">
                    {t('about.aboutPage.missionSection.paragraph1')}
                  </p>
                  <p className="mb-6 text-lg leading-relaxed">
                    {t('about.aboutPage.missionSection.paragraph2')}
                  </p>
                  <p className="mb-6 text-lg leading-relaxed">
                    {t('about.aboutPage.missionSection.paragraph3')}
                  </p>
                </div>
                <div className="flex items-center mt-6">
                  <MapPin className="h-5 w-5 text-gray-400 mr-2" />
                  <span className="text-base text-gray-500 dark:text-gray-400">
                    {t('about.aboutPage.missionSection.location')}
                  </span>
                </div>
              </Card>
            </motion.div>
          </div>

          {/* Core Values Section */}
          <motion.div
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.6, delay: 0.2 }}
            className="relative mb-16 z-10 max-w-4xl mx-auto"
          >
            <div className="grid grid-cols-1 md:grid-cols-3 gap-6">
              {coreValues.map((value, index) => (
                <Card
                  key={index}
                  className="p-6 hover:shadow-md transition-shadow duration-300 bg-white/90 dark:bg-gray-800/90"
                >
                  <div className="flex items-center justify-center w-12 h-12 rounded-full bg-green-50 dark:bg-green-900/20 mb-4">
                    {coreValueIcons[index]}
                  </div>
                  <h3 className="text-xl font-semibold mb-2 text-gray-900 dark:text-gray-100">
                    {value.title}
                  </h3>
                  <p className="text-gray-600 dark:text-gray-400">{value.description}</p>
                </Card>
              ))}
            </div>
          </motion.div>

          {/* Team Section */}
          <motion.div
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.6, delay: 0.3 }}
            className="relative mb-16 z-10 max-w-4xl mx-auto"
          >
            <div className="grid grid-cols-1 md:grid-cols-2 gap-8">
              {teamMembers.map((member, index) => (
                <Card
                  key={index}
                  className="overflow-hidden bg-white/90 dark:bg-gray-800/90 flex flex-col h-full"
                >
                  <div className="p-6 flex flex-col h-full">
                    <div>
                      <h3 className="text-xl font-semibold mb-1 text-gray-900 dark:text-gray-100">
                        {member.name}
                      </h3>
                      <p className="text-[#184c06] dark:text-green-400 mb-3">{member.title}</p>
                      <p className="text-gray-600 dark:text-gray-400 mb-4">{member.bio}</p>
                    </div>
                    <div className="mt-auto pt-4">
                      <a
                        href={member.linkedin}
                        target="_blank"
                        rel="noopener noreferrer"
                        className="inline-flex items-center text-sm text-[#229302] hover:text-[#184c06] dark:text-green-400 dark:hover:text-green-300"
                        onClick={() => trackEvent('Team', 'LinkedInClick', member.name)}
                      >
                        <AtSign className="h-4 w-4 mr-1" />
                        {t('about.aboutPage.linkedInProfile')}
                      </a>
                    </div>
                  </div>
                </Card>
              ))}
            </div>
          </motion.div>

          {/* FAQ Section */}
          <motion.div
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.6, delay: 0.4 }}
            className="relative mb-16 z-10 max-w-4xl mx-auto"
          >
            <div className="space-y-4">
              {faqItems.map((item, index) => (
                <div
                  key={index}
                  className="border border-gray-200 dark:border-gray-700 rounded-lg overflow-hidden"
                >
                  <button
                    onClick={() => toggleAccordion(index)}
                    className="flex items-center justify-between w-full p-5 text-left bg-white dark:bg-gray-800 hover:bg-gray-50 dark:hover:bg-gray-700 transition-colors duration-200"
                  >
                    <span className="text-lg font-medium text-gray-900 dark:text-white">
                      {item.question}
                    </span>
                    <span className="ml-6 flex-shrink-0 text-gray-500 dark:text-gray-400">
                      {activeAccordion === index ? (
                        <ChevronUp className="h-5 w-5" />
                      ) : (
                        <ChevronDown className="h-5 w-5" />
                      )}
                    </span>
                  </button>
                  {activeAccordion === index && (
                    <div className="p-5 border-t border-gray-200 dark:border-gray-700 bg-white dark:bg-gray-800">
                      <p className="text-gray-600 dark:text-gray-400">{item.answer}</p>
                    </div>
                  )}
                </div>
              ))}
            </div>
          </motion.div>

          {/* Call to Action */}
          <motion.div
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.6, delay: 0.5 }}
            className="relative mb-16 z-10 max-w-4xl mx-auto text-center"
          >
            <Card className="p-8 border-green-200 bg-gradient-to-br from-green-50 to-white dark:from-gray-800 dark:to-gray-900 dark:border-green-900/20">
              <h3 className="text-2xl font-semibold mb-4 text-gray-900 dark:text-gray-100">
                {t('about.aboutPage.cta.heading')}
              </h3>
              <p className="text-lg text-gray-600 dark:text-gray-400 mb-6 max-w-2xl mx-auto">
                {t('about.aboutPage.cta.subheading')}
              </p>
              <Link
                to="/contact"
                className="inline-block mx-auto"
                onClick={() => trackEvent('CTA', 'Click', 'About to Contact')}
              >
                <Button
                  variant="default"
                  size="lg"
                  className="bg-[#184c06] hover:bg-[#229302] text-white w-auto px-6"
                >
                  {t('about.aboutPage.cta.button')} <ArrowRight className="ml-2 h-4 w-4" />
                </Button>
              </Link>
            </Card>
          </motion.div>
        </div>
      </main>

      <Footer />
    </div>
  );
};

export default About;
